import type { RecipientRole } from '../api'
import {
  type InsertKind,
  type WorkflowApplyResult,
  type WorkflowDraft,
  type WorkflowOrder,
  type WorkflowParticipant,
  type WorkflowStage,
  WORKFLOW_TONES,
} from './types'

let draftSeq = 0
/** Number the canvas's ids from one again. */
export function resetWorkflowIds() {
  draftSeq = 0
}
const uid = (prefix: string) => `${prefix}_${++draftSeq}`

/**
 * The highest number any of the form's keys carries in the canvas's own
 * spelling of an id (p_4, st_2, br_1), or zero.
 *
 * The sending page keeps a canvas id as a person's key: each member of a
 * group made on the canvas, and a person added there alone. The canvas
 * numbered its ids from one on every visit, so a second visit could mint p_3
 * for a new signer while a group member from the first already held p_3, and
 * applyWorkflowDraft then took the new person for that member: the new signer
 * replaced the member in the group, and the group was listed a second time
 * under the new signer's name. Counting on from the highest keeps every id the
 * canvas mints apart from every key the form already holds.
 */
function highestCanvasId(recipients: EnvelopeRecipient[]): number {
  let highest = 0
  for (const r of recipients) {
    for (const key of [r.key, ...(r.members ?? []).map((m) => m.key)]) {
      const n = /^(?:p|st|br)_(\d+)$/.exec(key)
      if (n) highest = Math.max(highest, Number(n[1]))
    }
  }
  return highest
}

export function signingOrder(draft: WorkflowDraft): string[] {
  const ids: string[] = []
  for (const stage of draft.stages) {
    if (stage.kind === 'solo') ids.push(stage.participantId)
    if (stage.kind === 'chain') ids.push(...stage.participantIds)
    if (stage.kind === 'group') ids.push(...stage.participantIds)
    if (stage.kind === 'branch') ids.push(stage.targetParticipantId)
  }
  return ids
}

export function insertIndex(draft: WorkflowDraft, selectedId: string | null): number {
  if (!selectedId) {
    const notify = draft.stages.findIndex((s) => s.kind === 'notify')
    return notify >= 0 ? notify : draft.stages.length
  }
  for (let i = 0; i < draft.stages.length; i++) {
    const s = draft.stages[i]
    if (s.id === selectedId || s.kind === 'branch' && s.branchId === selectedId) return i + 1
    if (s.kind === 'solo' && s.participantId === selectedId) return i + 1
    if (s.kind === 'chain' && s.participantIds.includes(selectedId)) return i + 1
    if (s.kind === 'group' && (s.participantIds.includes(selectedId) || selectedId === `group:${s.id}`)) return i + 1
    if (s.kind === 'branch' && s.targetParticipantId === selectedId) return i + 1
    if (s.kind === 'notify' && s.participantIds.includes(selectedId)) return i
  }
  return draft.stages.length
}

export function stageForSelection(draft: WorkflowDraft, selectedId: string | null): WorkflowStage | null {
  if (!selectedId) return null
  for (const stage of draft.stages) {
    if (stage.id === selectedId || stage.kind === 'branch' && stage.branchId === selectedId) return stage
    if (stage.kind === 'solo' && stage.participantId === selectedId) return stage
    if (stage.kind === 'chain' && stage.participantIds.includes(selectedId)) return stage
    if (stage.kind === 'group' && (stage.participantIds.includes(selectedId) || selectedId === `group:${stage.id}`)) return stage
    if (stage.kind === 'branch' && stage.targetParticipantId === selectedId) return stage
    if (stage.kind === 'notify' && stage.participantIds.includes(selectedId)) return stage
  }
  return null
}

export function personById(draft: WorkflowDraft, id: string) {
  return draft.participants.find((p) => p.id === id)
}

function newParticipant(
  draft: WorkflowDraft,
  role: WorkflowParticipant['role'] = 'signer',
  group?: string,
): WorkflowParticipant {
  const n = draft.participants.filter((p) => p.role === 'signer' || p.role === 'witness').length + 1
  return {
    id: uid('p'),
    name: role === 'cc' || role === 'needs_to_view' ? 'Copy recipient' : `Signer ${n}`,
    email: '',
    role,
    tone: draft.participants.length % WORKFLOW_TONES.length,
    group,
  }
}

function insertStage(draft: WorkflowDraft, at: number, stage: WorkflowStage): WorkflowDraft {
  const stages = [...draft.stages]
  stages.splice(at, 0, stage)
  return { ...draft, stages }
}

export function addSigner(draft: WorkflowDraft, at: number): WorkflowDraft {
  const p = newParticipant(draft)
  return insertStage(
    { ...draft, participants: [...draft.participants, p] },
    at,
    { id: uid('st'), kind: 'solo', title: 'Signer', participantId: p.id },
  )
}

export function addGroup(draft: WorkflowDraft, at: number): WorkflowDraft {
  const label = 'Signing group'
  const a = newParticipant(draft, 'signer', label)
  const b = newParticipant(draft, 'signer', label)
  return insertStage(
    { ...draft, participants: [...draft.participants, a, b] },
    at,
    {
      id: uid('st'), kind: 'group', title: label, detail: 'Any 1 of 2', groupLabel: label,
      participantIds: [a.id, b.id],
    },
  )
}

export function addBranch(draft: WorkflowDraft, at: number): WorkflowDraft {
  const prior = draft.stages[at - 1]
  const afterId = prior?.kind === 'solo' ? prior.participantId
    : prior?.kind === 'chain' ? prior.participantIds.at(-1) ?? ''
      : ''
  const target = newParticipant(draft)
  const withPerson = { ...draft, participants: [...draft.participants, target] }
  return insertStage(withPerson, at, {
    id: uid('st'),
    kind: 'branch',
    title: 'If Amount is over 50000',
    detail: 'Only one path runs',
    branchId: uid('br'),
    afterParticipantId: afterId,
    targetParticipantId: target.id,
    field: 'Amount',
    value: '50000',
  })
}

export function addNotify(draft: WorkflowDraft, at: number): WorkflowDraft {
  const p = newParticipant(draft, 'cc')
  const notifyIdx = draft.stages.findIndex((s) => s.kind === 'notify')
  const stage: WorkflowStage = notifyIdx >= 0
    ? {
        ...draft.stages[notifyIdx] as Extract<WorkflowStage, { kind: 'notify' }>,
        participantIds: [
          ...(draft.stages[notifyIdx] as Extract<WorkflowStage, { kind: 'notify' }>).participantIds,
          p.id,
        ],
      }
    : { id: uid('st'), kind: 'notify', title: 'Notified when finished', participantIds: [p.id] }
  if (notifyIdx >= 0) {
    const stages = draft.stages.map((s, i) => (i === notifyIdx ? stage : s))
    return { ...draft, participants: [...draft.participants, p], stages }
  }
  return insertStage({ ...draft, participants: [...draft.participants, p] }, at, stage)
}

export function removeStage(draft: WorkflowDraft, stageId: string): WorkflowDraft {
  const stage = draft.stages.find((s) => s.id === stageId)
  if (!stage) return draft
  const dropIds = new Set<string>()
  if (stage.kind === 'solo') dropIds.add(stage.participantId)
  if (stage.kind === 'chain') stage.participantIds.forEach((id) => dropIds.add(id))
  if (stage.kind === 'group') stage.participantIds.forEach((id) => dropIds.add(id))
  if (stage.kind === 'branch') dropIds.add(stage.targetParticipantId)
  if (stage.kind === 'notify') stage.participantIds.forEach((id) => dropIds.add(id))
  return {
    ...draft,
    stages: draft.stages.filter((s) => s.id !== stageId),
    participants: draft.participants.filter((p) => !dropIds.has(p.id)),
  }
}

export function removeParticipant(draft: WorkflowDraft, participantId: string): WorkflowDraft {
  const signers = draft.participants.filter((p) => p.role === 'signer' || p.role === 'witness')
  const target = draft.participants.find((p) => p.id === participantId)
  if (!target) return draft
  if ((target.role === 'signer' || target.role === 'witness') && signers.length <= 1) return draft

  return {
    ...draft,
    participants: draft.participants.filter((p) => p.id !== participantId),
    stages: draft.stages.map((stage) => {
      if (stage.kind === 'solo' && stage.participantId === participantId) return null
      if (stage.kind === 'chain') {
        const participantIds = stage.participantIds.filter((id) => id !== participantId)
        if (participantIds.length === 0) return null
        if (participantIds.length === 1) {
          return { id: stage.id, kind: 'solo' as const, title: stage.title, participantId: participantIds[0] }
        }
        return { ...stage, participantIds }
      }
      if (stage.kind === 'group') {
        const participantIds = stage.participantIds.filter((id) => id !== participantId)
        if (participantIds.length < 2) return null
        return { ...stage, participantIds }
      }
      if (stage.kind === 'branch' && stage.targetParticipantId === participantId) return null
      if (stage.kind === 'notify') {
        const participantIds = stage.participantIds.filter((id) => id !== participantId)
        if (participantIds.length === 0) return null
        return { ...stage, participantIds }
      }
      return stage
    }).filter(Boolean) as WorkflowStage[],
  }
}

/**
 * The server's limits, the same on the canvas as on the sending page's list:
 * an envelope carries at most twenty participations, each member of a signing
 * group being one, and a group has two to five members (MaxRecipients and
 * MaxSigningGroupMembers on the server). The canvas checked neither, so it
 * could design an envelope the server then refused.
 */
export const MAX_PARTICIPANTS = 20
export const MAX_GROUP_MEMBERS = 5

/** How many people a stage of each kind adds: a group starts with two. */
const PEOPLE_ADDED: Record<InsertKind, number> = { signer: 1, branch: 1, group: 2, notify: 1 }

/** Whether a stage of this kind still fits under the limit. */
export function canInsert(draft: WorkflowDraft, kind: InsertKind): boolean {
  return draft.participants.length + PEOPLE_ADDED[kind] <= MAX_PARTICIPANTS
}

/** Whether this group can take one more member. */
export function canAddGroupMember(draft: WorkflowDraft, stageId: string): boolean {
  const stage = draft.stages.find((s) => s.id === stageId && s.kind === 'group')
  return stage?.kind === 'group'
    && stage.participantIds.length < MAX_GROUP_MEMBERS
    && draft.participants.length < MAX_PARTICIPANTS
}

export function addGroupMember(draft: WorkflowDraft, stageId: string): WorkflowDraft {
  const stage = draft.stages.find((s) => s.id === stageId && s.kind === 'group')
  if (!stage || stage.kind !== 'group' || !canAddGroupMember(draft, stageId)) return draft
  const p = newParticipant(draft, 'signer', stage.groupLabel)
  return {
    ...draft,
    participants: [...draft.participants, p],
    stages: draft.stages.map((s) => (
      s.id === stageId && s.kind === 'group'
        ? { ...s, participantIds: [...s.participantIds, p.id], detail: `Any 1 of ${s.participantIds.length + 1}` }
        : s
    )),
  }
}

/** A stage inserted where the canvas was asked, or nothing past the limit (canInsert). */
export function insertWorkflow(draft: WorkflowDraft, kind: InsertKind, at: number): WorkflowDraft {
  if (!canInsert(draft, kind)) return draft
  if (kind === 'signer') return addSigner(draft, at)
  if (kind === 'branch') return addBranch(draft, at)
  if (kind === 'group') return addGroup(draft, at)
  return addNotify(draft, at)
}

/** Recipient row as stored in the new-envelope draft. */
export type EnvelopeRecipient = {
  key: string
  name: string
  email: string
  role: RecipientRole
  isGroup?: boolean
  members?: { key: string; name: string; email: string }[]
}

export function draftFromRecipients(
  recipients: EnvelopeRecipient[],
  documentTitle: string,
  toneAt: (index: number) => number,
): WorkflowDraft {
  draftSeq = highestCanvasId(recipients)
  const participants: WorkflowParticipant[] = []
  const stages: WorkflowStage[] = []
  const notifyIds: string[] = []

  for (const [i, r] of recipients.entries()) {
    if (r.role === 'cc' || r.role === 'needs_to_view') {
      participants.push({
        id: r.key,
        name: r.name,
        email: r.email,
        role: r.role,
        tone: toneAt(i),
      })
      notifyIds.push(r.key)
      continue
    }
    if (r.isGroup && r.members) {
      const memberIds = r.members.map((m) => {
        participants.push({
          id: m.key,
          name: m.name,
          email: m.email,
          role: r.role,
          tone: toneAt(participants.length),
          group: r.name.trim() || 'Signing group',
        })
        return m.key
      })
      stages.push({
        id: uid('st'),
        kind: 'group',
        title: r.name.trim() || 'Signing group',
        detail: `Any 1 of ${memberIds.length}`,
        groupLabel: r.name.trim() || 'Signing group',
        participantIds: memberIds,
      })
      continue
    }
    participants.push({
      id: r.key,
      name: r.name,
      email: r.email,
      role: r.role as WorkflowParticipant['role'],
      tone: toneAt(i),
    })
    stages.push({
      id: uid('st'),
      kind: 'solo',
      title: r.role === 'witness' ? 'Witness' : 'Signer',
      participantId: r.key,
    })
  }

  if (notifyIds.length) {
    stages.push({
      id: uid('st'),
      kind: 'notify',
      title: 'Notified when finished',
      participantIds: notifyIds,
    })
  }

  if (!stages.length && participants.length) {
    stages.push({
      id: uid('st'),
      kind: 'solo',
      title: 'Signer',
      participantId: participants[0].id,
    })
  }

  return { document: documentTitle, participants, stages }
}

export function draftWithRouting(
  base: WorkflowDraft,
  routingEnabled: boolean,
  routingThreshold: string,
  triggerKey: string,
  branchTargetKey: string,
): WorkflowDraft {
  if (!routingEnabled) return base
  const triggerIdx = base.stages.findIndex(
    (s) => s.kind === 'solo' && s.participantId === triggerKey,
  )
  if (triggerIdx < 0) return base
  const branchStage: WorkflowStage = {
    id: uid('st'),
    kind: 'branch',
    title: `If Amount is over ${routingThreshold}`,
    detail: 'Only one path runs',
    branchId: uid('br'),
    afterParticipantId: triggerKey,
    targetParticipantId: branchTargetKey,
    field: 'Amount',
    value: routingThreshold,
  }
  const stages = [...base.stages]
  stages.splice(triggerIdx + 1, 0, branchStage)
  return { ...base, stages }
}

export function applyWorkflowDraft<T extends EnvelopeRecipient>(
  draft: WorkflowDraft,
  existing: T[],
  createRecipient: (participant: WorkflowParticipant, members?: WorkflowParticipant[]) => T,
): { recipients: T[]; routing: WorkflowApplyResult } {
  const byId = new Map(existing.map((r) => [r.key, r]))
  const byMemberKey = new Map<string, T>()
  for (const r of existing) {
    if (r.isGroup && r.members) {
      for (const m of r.members) byMemberKey.set(m.key, r)
    }
  }

  const findExisting = (id: string): T | undefined => byId.get(id) ?? byMemberKey.get(id)

  const branch = draft.stages.find((s): s is Extract<WorkflowStage, { kind: 'branch' }> => s.kind === 'branch')
  const recipients: T[] = []
  const used = new Set<string>()

  for (const stage of draft.stages) {
    if (stage.kind === 'branch') {
      const p = personById(draft, stage.targetParticipantId)
      if (p && !used.has(p.id)) {
        used.add(p.id)
        const prior = findExisting(p.id)
        recipients.push(prior
          ? { ...prior, name: p.name, email: p.email, role: p.role }
          : createRecipient(p))
      }
      continue
    }
    if (stage.kind === 'notify') {
      for (const id of stage.participantIds) {
        const p = personById(draft, id)
        if (!p || used.has(id)) continue
        used.add(id)
        const prior = findExisting(id)
        recipients.push(prior
          ? { ...prior, name: p.name, email: p.email, role: p.role }
          : createRecipient(p))
      }
      continue
    }
    if (stage.kind === 'solo') {
      const p = personById(draft, stage.participantId)
      if (!p) continue
      used.add(p.id)
      const prior = findExisting(p.id)
      recipients.push(prior
        ? { ...prior, name: p.name, email: p.email, role: p.role }
        : createRecipient(p))
      continue
    }
    if (stage.kind === 'chain') {
      for (const id of stage.participantIds) {
        const p = personById(draft, id)
        if (!p) continue
        used.add(id)
        const prior = findExisting(id)
        recipients.push(prior
          ? { ...prior, name: p.name, email: p.email, role: p.role }
          : createRecipient(p))
      }
      continue
    }
    if (stage.kind === 'group') {
      const members = stage.participantIds
        .map((id) => personById(draft, id))
        .filter(Boolean) as WorkflowParticipant[]
      members.forEach((m) => used.add(m.id))
      const prior = findExisting(stage.participantIds[0] ?? '')
      if (prior?.isGroup && prior.members) {
        recipients.push({
          ...prior,
          name: stage.title,
          members: members.map((m) => {
            const ex = prior.members?.find((x) => x.key === m.id)
            return ex
              ? { ...ex, name: m.name, email: m.email }
              : { key: m.id, name: m.name, email: m.email }
          }),
        } as T)
      } else {
        recipients.push(createRecipient(
          { ...members[0], name: stage.title, email: '' },
          members,
        ))
      }
    }
  }

  for (const p of draft.participants) {
    if (!used.has(p.id) && p.role !== 'cc' && p.role !== 'needs_to_view') {
      const prior = findExisting(p.id)
      recipients.push(prior
        ? { ...prior, name: p.name, email: p.email, role: p.role }
        : createRecipient(p))
    }
  }

  const routing: WorkflowApplyResult = {
    order: 'sequential' as WorkflowOrder,
    routingEnabled: Boolean(branch),
    routingThreshold: branch?.value ?? '50000',
    routingFieldLabel: branch?.field ?? 'Amount',
  }

  return { recipients, routing }
}
